import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
	ProgramOwnerCheckoutAssignmentDto,
	ServicesProvider,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to update checkout assignment configuration for a services provider
 */
export const updateCheckoutAssignment = async (
	servicesProviderId: string,
	data: ProgramOwnerCheckoutAssignmentDto,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.checkoutAssignment(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for updating checkout assignment configuration for a services provider
 */
export const useUpdateCheckoutAssignment = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: ProgramOwnerCheckoutAssignmentDto) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to update checkout assignment without a services provider ID.',
				);
			}
			return updateCheckoutAssignment(servicesProviderId, data);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(
				queries.servicesProviders.detail(data.id).queryKey,
				data,
			);
		},
	});
};
